import React, { useState } from 'react';
import { 
  AppBar, 
  Toolbar, 
  IconButton, 
  Typography, 
  Box, 
  Avatar, 
  useTheme, 
  Badge, 
  Menu, 
  MenuItem, 
  ListItemIcon, 
  Divider,
  Tooltip,
  alpha
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import NotificationsIcon from '@mui/icons-material/Notifications';
import SettingsIcon from '@mui/icons-material/Settings';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import ThemeToggle from './ThemeToggle';
import LogoutButton from './LogoutButton';
import { useThemeContext } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';

interface TopbarProps {
  onMenuToggle: () => void;
}

const Topbar: React.FC<TopbarProps> = ({ onMenuToggle }) => {
  const theme = useTheme();
  const { mode } = useThemeContext();
  const isDark = mode === 'dark';
  
  const [profileAnchorEl, setProfileAnchorEl] = useState<null | HTMLElement>(null);
  const [notificationsAnchorEl, setNotificationsAnchorEl] = useState<null | HTMLElement>(null);
  
  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setProfileAnchorEl(event.currentTarget);
  };
  
  const handleNotificationsMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setNotificationsAnchorEl(event.currentTarget);
  };
  
  const handleMenuClose = () => {
    setProfileAnchorEl(null);
    setNotificationsAnchorEl(null);
  };

  const notificationItems = [
    { id: 1, message: 'New company registered', time: '2 minutes ago' },
    { id: 2, message: 'Monthly report available', time: '1 hour ago' },
    { id: 3, message: 'System update completed', time: 'Yesterday' },
  ];

  return (
    <AppBar 
      position="sticky" 
      sx={{ 
        boxShadow: '0 1px 3px rgba(0,0,0,0.05), 0 1px 2px rgba(0,0,0,0.1)', 
        bgcolor: theme.palette.background.paper, 
        color: theme.palette.text.primary,
        "& .MuiPaper-elevation": {
          borderRadius: "0 !important" 
        }
      }}
    >
      <Toolbar sx={{ minHeight: '64px' }}>
        <IconButton 
          color="inherit" 
          onClick={onMenuToggle} 
          edge="start" 
          sx={{ 
            mr: 2,
            borderRadius: 1.5,
            '&:hover': {
              backgroundColor: alpha(theme.palette.primary.main, 0.08),
            },
          }}
        >
          <MenuIcon />
        </IconButton>
        
        <Typography 
          variant="h6" 
          component="div" 
          sx={{ 
            flexGrow: 1, 
            fontWeight: 600,
            display: 'flex',
            alignItems: 'center', 
          }}
        >
          <Box component="span">Dashboard</Box>
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <ThemeToggle />
          
          {/* <Tooltip title="Help">
            <IconButton 
              color="inherit" 
              sx={{ 
                mx: 0.5,
                '&:hover': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.08),
                },
              }}
            >
              <HelpOutlineIcon />
            </IconButton>
          </Tooltip> */}
          
          {/* <Tooltip title="Notifications">
            <IconButton 
              color="inherit" 
              onClick={handleNotificationsMenuOpen}
              sx={{ 
                mx: 0.5,
                '&:hover': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.08),
                },
              }}
            >
              <Badge badgeContent={3} color="error">
                <NotificationsIcon />
              </Badge>
            </IconButton>
          </Tooltip> */}
          
          {/* <Tooltip title="Settings">
            <IconButton 
              color="inherit" 
              sx={{ 
                mx: 0.5,
                '&:hover': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.08),
                },
              }}
            >
              <SettingsIcon />
            </IconButton>
          </Tooltip> */}
          
          <Box 
            sx={{ 
              ml: 0.5, 
              display: 'flex', 
              alignItems: 'center', 
              cursor: 'pointer',
              borderRadius: 2,
              px: 1,
              py: 0.5,
              '&:hover': {
                backgroundColor: alpha(theme.palette.primary.main, 0.08),
              },
            }}
            onClick={handleProfileMenuOpen}
          >
            <Avatar 
              alt="Admin User" 
              src="/static/avatar.jpg" 
              sx={{ 
                width: 36, 
                height: 36,
                border: `2px solid ${theme.palette.primary.main}`,
              }}
            />
            <Box sx={{ ml: 1, display: { xs: 'none', sm: 'block' } }}>
              <Typography 
                variant="body2" 
                sx={{ 
                  fontWeight: 600,
                  lineHeight: 1.2,
                }}
              >
                Admin User
              </Typography>
              <Typography 
                variant="caption" 
                sx={{ 
                  color: theme.palette.text.secondary,
                  lineHeight: 1.2,
                }}
              >
                Administrator
              </Typography>
            </Box>
          </Box>
        </Box>
      </Toolbar>
      
      {/* Profile Menu */}
      <Menu
        anchorEl={profileAnchorEl}
        open={Boolean(profileAnchorEl)}
        onClose={handleMenuClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        PaperProps={{
          sx: {
            mt: 1.5,
            boxShadow: '0 8px 16px rgba(0,0,0,0.1)',
            minWidth: 200,
          },
        }}
      >
        <MenuItem onClick={handleMenuClose}>
          <ListItemIcon>
            <AccountCircleIcon fontSize="small" color="primary" />
          </ListItemIcon>
          Profile
        </MenuItem>
        <MenuItem onClick={handleMenuClose}>
          <ListItemIcon>
            <SettingsIcon fontSize="small" color="primary" />
          </ListItemIcon>
          Settings
        </MenuItem>
        <Divider />
        <LogoutButton onClick={handleMenuClose} />
      </Menu>
      
      {/* Notifications Menu */}
      <Menu
        anchorEl={notificationsAnchorEl}
        open={Boolean(notificationsAnchorEl)}
        onClose={handleMenuClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        PaperProps={{
          sx: {
            mt: 1.5,
            boxShadow: '0 8px 16px rgba(0,0,0,0.1)',
            minWidth: 280,
          },
        }}
      >
        <Box sx={{ px: 2, py: 1 }}>
          <Typography variant="subtitle1" fontWeight={600}>Notifications</Typography>
        </Box>
        <Divider />
        {notificationItems.map((item) => (
          <MenuItem key={item.id} onClick={handleMenuClose} sx={{ py: 1.5 }}>
            <Box>
              <Typography variant="body2">{item.message}</Typography>
              <Typography variant="caption" color="text.secondary">{item.time}</Typography>
            </Box>
          </MenuItem>
        ))}
        <Divider />
        <MenuItem onClick={handleMenuClose} sx={{ justifyContent: 'center' }}>
          <Typography variant="body2" color="primary">View All Notifications</Typography>
        </MenuItem>
      </Menu>
    </AppBar>
  );
};

export default Topbar;